/**
 * <four-worlds-gallery> — large synced image stage + clickable index list.
 * Expects children marked up by sections/four-worlds.liquid:
 *   .fw-stage-media[data-index], .fw-stage-copy-item[data-index] (toggle .is-active)
 *   .fw-list-item[data-index] (toggle .is-active, contains .fw-list-progress-fill)
 * All three groups advance together — one index drives image, copy, and list state.
 * Fully self-contained: no shared state with cart/facets/product-form.
 */
class FourWorldsGallery extends HTMLElement {
  #index = 0;
  #timer = null;
  #paused = false;

  connectedCallback() {
    this.media = [...this.querySelectorAll('.fw-stage-media')];
    this.copies = [...this.querySelectorAll('.fw-stage-copy-item')];
    this.items = [...this.querySelectorAll('.fw-list-item')];
    this.duration = Number(this.dataset.autoplaySpeed || 6000);

    this.style.setProperty('--fw-duration', `${this.duration}ms`);

    this.items.forEach((item, i) => {
      item.addEventListener('click', () => {
        this.#goTo(i);
        this.#restart();
      });
    });

    this.addEventListener('pointerenter', () => this.#setPaused(true));
    this.addEventListener('pointerleave', () => this.#setPaused(false));
    this.addEventListener('focusin', () => this.#setPaused(true));
    this.addEventListener('focusout', () => this.#setPaused(false));

    this.#render();
    this.#restart();
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
  }

  #setPaused(paused) {
    this.#paused = paused;
    this.style.setProperty('--fw-play-state', paused ? 'paused' : 'running');
    if (paused) {
      clearTimeout(this.#timer);
    } else {
      this.#restart();
    }
  }

  #restart() {
    clearTimeout(this.#timer);
    if (this.#paused || !this.media.length) return;
    this.#timer = setTimeout(() => {
      this.#goTo(this.#index + 1);
      this.#restart();
    }, this.duration);
  }

  #goTo(next) {
    const count = this.media.length;
    this.#index = ((next % count) + count) % count;
    this.#render();
  }

  #render() {
    this.media.forEach((el, i) => el.classList.toggle('is-active', i === this.#index));
    this.copies.forEach((el, i) => {
      el.classList.toggle('is-active', i === this.#index);
      el.setAttribute('aria-hidden', i === this.#index ? 'false' : 'true');
    });
    this.items.forEach((el, i) => {
      const active = i === this.#index;
      el.classList.toggle('is-active', active);
      el.setAttribute('aria-selected', active ? 'true' : 'false');
      // Restart the CSS fill animation on the newly active row only. A
      // rAF tick (rather than a synchronous offsetHeight read) still
      // guarantees the browser has applied `animation: none` before we
      // clear it, without forcing a synchronous layout/reflow on every
      // slide change.
      const fill = el.querySelector('.fw-list-progress-fill');
      if (fill && active) {
        fill.style.animation = 'none';
        requestAnimationFrame(() => {
          fill.style.animation = '';
        });
      }
    });
  }
}

customElements.define('four-worlds-gallery', FourWorldsGallery);
